import { MANAGER_ROLES } from '@performance-crm/shared';
import { Copy, FileUp, GitMerge, Inbox, Map, Webhook } from 'lucide-react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Select } from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import { useCompanyScope } from '@/lib/useCompanyScope';
import { DuplicatesTab } from './DuplicatesTab';
import { EventsTab } from './EventsTab';
import { ImportTab } from './ImportTab';
import { MappingsTab } from './MappingsTab';
import { WebhooksTab } from './WebhooksTab';

type Tab = 'eventos' | 'mapeos' | 'importar' | 'duplicados' | 'webhooks';
const TABS: Array<[Tab, string, typeof Inbox]> = [
  ['eventos', 'Eventos', Inbox],
  ['mapeos', 'Fuentes y mapeos', Map],
  ['importar', 'Importar Excel / CSV', FileUp],
  ['duplicados', 'Duplicados', GitMerge],
  ['webhooks', 'Webhooks salientes', Webhook],
];

export function IngestPage() {
  const { user } = useAuth();
  const { companies, active } = useCompanyScope();
  const [params, setParams] = useSearchParams();
  const companyId =
    params.get('companyId') ??
    active ??
    companies.find((c) => MANAGER_ROLES.includes(c.role))?.companyId;
  const tab = (params.get('tab') as Tab) ?? 'eventos';
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  if (user && !user.memberships.some((m) => MANAGER_ROLES.includes(m.role)))
    return <Navigate to="/" replace />;
  const isManager = !!user?.memberships.some(
    (m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role),
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ingesta e integraciones</h1>
          <p className="text-muted-foreground text-sm">
            Leads que entran por webhook, Meta, CSV y el multiagente. Duplicados, mapeos y webhooks
            salientes.
          </p>
        </div>
        {companies.length > 1 && (
          <Select
            value={companyId ?? ''}
            onChange={(e) => set('companyId', e.target.value)}
            aria-label="Empresa"
            className="w-56"
          >
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName}
              </option>
            ))}
          </Select>
        )}
      </div>
      <div className="bg-card flex flex-wrap gap-1 rounded-md border p-1">
        {TABS.map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => set('tab', key)}
            className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm ${tab === key ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
            data-testid={`tab-${key}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {!isManager && (
        <p className="text-muted-foreground text-sm">No tienes rol de gestión en esta empresa.</p>
      )}
      {isManager && companyId && tab === 'eventos' && <EventsTab companyId={companyId} />}
      {isManager && companyId && tab === 'mapeos' && <MappingsTab companyId={companyId} />}
      {isManager && companyId && tab === 'importar' && <ImportTab companyId={companyId} />}
      {isManager && companyId && tab === 'duplicados' && <DuplicatesTab companyId={companyId} />}
      {isManager && companyId && tab === 'webhooks' && <WebhooksTab companyId={companyId} />}
    </div>
  );
}

export function CopyButton({ value }: { value: string }) {
  return (
    <button
      type="button"
      className="text-primary inline-flex items-center gap-1 text-xs underline"
      onClick={() => void navigator.clipboard?.writeText(value)}
    >
      <Copy className="h-3 w-3" /> copiar
    </button>
  );
}
